import { useCallback, useEffect, useRef } from 'react';

// The V2 upload flow posts through apollo-upload-client (fetch), which can't
// report real byte-level progress. Fake it so the bar never looks frozen: ease
// toward a ceiling and hold there until the real upload resolves and the caller
// snaps it to 100. Bandwidth is unknown, so we don't try to estimate a true
// duration from file size — that just makes the bar crawl when uploads are fast
// and race ahead when they're slow. A bounded easing curve reads well either way.
const TICK_MS = 100;
const CEILING = 90; // hold here until the upload actually completes
const TAU_MS = 2_500; // easing time constant: ~63% of the ceiling by here, then it decelerates

/**
 * Drives a faked, ever-advancing progress value while a V2 upload is in flight.
 * Call `start()` before awaiting the upload and `stop()` once it settles (then
 * set the final 0 or 100 yourself).
 */
export function useFakeUploadProgress(
  setProgress: React.Dispatch<React.SetStateAction<number>>
) {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Clear the interval if the uploader unmounts mid-upload.
  useEffect(() => stop, [stop]);

  const start = useCallback(() => {
    stop();
    const startTime = Date.now();
    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      setProgress(Math.round(CEILING * (1 - Math.exp(-elapsed / TAU_MS))));
    }, TICK_MS);
  }, [setProgress, stop]);

  return { start, stop };
}
